import { useState } from 'react';
import { Printer } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/pos-api';
import { goodsPrintHtml, goodsPrintModel, goodsExcelRows } from '@/lib/goods-print.mjs';
import { downloadWorkbook } from '@/lib/document-export';
import { usesWindowsPrinter } from '@/lib/printer-mode';

const A5_PRINT_HELPER = 'http://127.0.0.1:47631/print';

export default function GoodsPrintActions({ kind, document, disabled = false, onPrintDetail, onPrintInvoice }) {
  const [paper, setPaper] = useState('A5'), [busy, setBusy] = useState(false);
  const excel = async () => {
    setBusy(true);
    try {
      const model = goodsPrintModel(kind, document);
      await downloadWorkbook({ title: `${model.title} ${model.number}`.slice(0,150), headers: model.headers, rows: goodsExcelRows(model), paper,
        notes: [...model.meta.map(([k,v]) => `${k}: ${v ?? '—'}`), ...model.totals.map(([k,v]) => `${k}: ${v ?? 'Belum lengkap'}`), ...model.notes] });
    } catch { toast.error('Ekspor gagal. Periksa koneksi dan kelengkapan data.'); }
    finally { setBusy(false); }
  };
  const print = async () => {
    const useA5Helper = kind === 'transfer' && usesWindowsPrinter();
    const popup = useA5Helper ? null : window.open('', '_blank', 'width=1100,height=800');
    if (!useA5Helper && !popup) return toast.error('Izinkan popup browser untuk mencetak dokumen.');
    if (popup) {
      popup.opener = null;
      popup.document.body.textContent = 'Menyiapkan dokumen cetak…';
    }
    setBusy(true);
    try {
      let storeName = document.store_name || '';
      if (!storeName && document.store_id) {
        try { const { data } = await api.get('/stores'); storeName = data.find(s => s.id === document.store_id)?.name || ''; } catch { /* Keep the stored branch ID if its name is unavailable. */ }
      }
      const html = goodsPrintHtml(kind, document, { paper, storeName });
      if (useA5Helper) {
        const response = await fetch(A5_PRINT_HELPER, {
          method: 'POST', mode: 'cors',
          headers: { 'Content-Type': 'application/json', 'X-Masivi-Print': '1' },
          body: JSON.stringify({ html }),
        });
        if (!response.ok) throw new Error('Helper cetak A5 menolak dokumen. Jalankan ulang Pasang-POS.cmd dari paket helper A5.');
      } else {
        if (popup.closed) return;
        popup.document.open(); popup.document.write(html); popup.document.close(); popup.focus();
        popup.setTimeout(() => { if (!popup.closed) popup.print(); }, 150);
      }
    } catch (error) {
      if (popup && !popup.closed) popup.close();
      toast.error(useA5Helper
        ? 'Helper cetak A5 belum aktif atau izin jaringan lokal belum diberikan. Jalankan Pasang-POS.cmd satu kali dan izinkan akses lokal Chrome.'
        : 'Dokumen belum dapat dicetak. Pastikan data transaksi sudah tersimpan dan lengkap.');
    }
    finally { setBusy(false); }
  };
  return <div className="flex flex-wrap items-center gap-2" data-testid={`${kind}-print-actions`}>
    <label className="text-xs flex items-center gap-1">Kertas<select aria-label="Ukuran kertas dokumen" data-testid={`${kind}-print-paper`} value={paper} onChange={e => setPaper(e.target.value)} disabled={disabled || busy} className="h-9 rounded-md border border-ui-line bg-ui-card text-ui-text px-2"><option value="A5">A5</option><option value="A4">A4</option></select></label>
    <Button type="button" variant="outline" disabled={disabled || busy || !document?.id} onClick={print} data-testid={`${kind}-print-check`} className="gap-1"><Printer size={15} />{busy ? 'Menyiapkan…' : 'Cetak Cek Barang'}</Button>
    <Button type="button" variant="outline" disabled={disabled || busy || !document?.id} onClick={excel} data-testid={`${kind}-export-excel`}>Excel</Button>
    {onPrintDetail && <Button type="button" variant="outline" disabled={disabled || busy} onClick={() => onPrintDetail(paper)} data-testid="history-print-detail" className="gap-1"><Printer size={15} />Cetak Detail</Button>}
    {onPrintInvoice && <Button type="button" variant="outline" disabled={disabled || busy} onClick={() => onPrintInvoice(paper)} data-testid="history-print-invoice" className="gap-1"><Printer size={15} />Cetak Faktur</Button>}
  </div>;
}
